/**
 * Goals React Query Hooks
 * BP-06-03: Care Team & Goals Frontend
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { goalsApi } from '@/features/goals/api/goalsApi';
import type {
  Goal,
  GoalProgressEntry,
  CreateGoalRequest,
  UpdateGoalRequest,
  CreateProgressEntryRequest,
  UpdateProgressEntryRequest,
  GoalFilters,
  GoalSortOptions,
  GoalsExportOptions,
} from '@/features/goals/types/goalsTypes';

// ============================================================================
// Query Keys
// ============================================================================

export const goalsKeys = {
  all: ['goals'] as const,
  lists: () => [...goalsKeys.all, 'list'] as const,
  list: (childId: string, filters?: GoalFilters, sort?: GoalSortOptions) => 
    [...goalsKeys.lists(), childId, filters, sort] as const,
  details: () => [...goalsKeys.all, 'detail'] as const,
  detail: (goalId: string) => [...goalsKeys.details(), goalId] as const,
  progress: (goalId: string) => [...goalsKeys.all, 'progress', goalId] as const,
  summary: (childId: string) => [...goalsKeys.all, 'summary', childId] as const,
  iepReport: (childId: string) => [...goalsKeys.all, 'iep-report', childId] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch all goals for a child
 */
export function useGoals(
  childId: string,
  filters?: GoalFilters,
  sort?: GoalSortOptions,
  page?: number,
  limit?: number
) {
  return useQuery({
    queryKey: goalsKeys.list(childId, filters, sort),
    queryFn: () => goalsApi.getAll(childId, filters, sort, page, limit),
    enabled: !!childId,
  });
}

/**
 * Hook to fetch a single goal with details
 */
export function useGoal(goalId: string | null) {
  return useQuery({
    queryKey: goalsKeys.detail(goalId!),
    queryFn: () => goalsApi.getById(goalId!),
    enabled: !!goalId,
  });
}

/**
 * Hook to fetch progress entries for a goal
 */
export function useGoalProgress(goalId: string | null, page?: number, limit?: number) {
  return useQuery({
    queryKey: goalsKeys.progress(goalId!),
    queryFn: () => goalsApi.getProgress(goalId!, page, limit),
    enabled: !!goalId,
  });
}

/**
 * Hook to fetch goals summary for a child
 */
export function useGoalsSummary(childId: string) {
  return useQuery({
    queryKey: goalsKeys.summary(childId),
    queryFn: () => goalsApi.getSummary(childId),
    enabled: !!childId,
  });
}

/**
 * Hook to fetch IEP goals report
 */
export function useIEPReport(childId: string) {
  return useQuery({
    queryKey: goalsKeys.iepReport(childId),
    queryFn: () => goalsApi.getIEPReport(childId),
    enabled: !!childId,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to create a new goal
 */
export function useCreateGoal(childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGoalRequest) => goalsApi.create(childId, data),
    onSuccess: () => {
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: goalsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: goalsKeys.summary(childId) });
    },
  });
}

/**
 * Hook to update a goal
 */
export function useUpdateGoal(childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ goalId, data }: { goalId: string; data: UpdateGoalRequest }) =>
      goalsApi.update(goalId, data),
    onSuccess: (updatedGoal, { goalId }) => {
      // Update the detail cache
      queryClient.setQueryData(goalsKeys.detail(goalId), updatedGoal);
      
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: goalsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: goalsKeys.summary(childId) });
    },
  });
}

/**
 * Hook to delete a goal
 */
export function useDeleteGoal(childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goalId: string) => goalsApi.delete(goalId),
    onSuccess: (_, goalId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: goalsKeys.detail(goalId) });
      queryClient.removeQueries({ queryKey: goalsKeys.progress(goalId) });
      
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: goalsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: goalsKeys.summary(childId) });
    },
  });
}

/**
 * Hook to mark a goal as achieved
 */
export function useMarkGoalAchieved(childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goalId: string) => goalsApi.markAchieved(goalId),
    onSuccess: (updatedGoal, goalId) => {
      queryClient.setQueryData(goalsKeys.detail(goalId), updatedGoal);
      queryClient.invalidateQueries({ queryKey: goalsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: goalsKeys.summary(childId) });
    },
  });
}

/**
 * Hook to discontinue a goal (with childId)
 */
export function useDiscontinueGoal(childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ goalId, reason }: { goalId: string; reason?: string }) =>
      goalsApi.discontinue(goalId, reason),
    onSuccess: (updatedGoal, { goalId }) => {
      queryClient.setQueryData(goalsKeys.detail(goalId), updatedGoal);
      queryClient.invalidateQueries({ queryKey: goalsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: goalsKeys.summary(childId) });
    },
  });
}

/**
 * Hook to add a progress entry
 */
export function useAddProgressEntry(goalId: string, childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProgressEntryRequest) =>
      goalsApi.addProgress(goalId, data),
    onSuccess: () => {
      // Invalidate progress and detail queries
      queryClient.invalidateQueries({ queryKey: goalsKeys.progress(goalId) });
      queryClient.invalidateQueries({ queryKey: goalsKeys.detail(goalId) });
      queryClient.invalidateQueries({ queryKey: goalsKeys.summary(childId) });
    },
  });
}

/**
 * Hook to update a progress entry
 */
export function useUpdateProgressEntry(goalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entryId, data }: { entryId: string; data: UpdateProgressEntryRequest }) =>
      goalsApi.updateProgress(entryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.progress(goalId) });
      queryClient.invalidateQueries({ queryKey: goalsKeys.detail(goalId) });
    },
  });
}

/**
 * Hook to delete a progress entry
 */
export function useDeleteProgressEntry(goalId: string, childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryId: string) => goalsApi.deleteProgress(entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.progress(goalId) });
      queryClient.invalidateQueries({ queryKey: goalsKeys.detail(goalId) });
      queryClient.invalidateQueries({ queryKey: goalsKeys.summary(childId) });
    },
  });
}

/**
 * Hook to export goals
 */
export function useExportGoals(childId: string) {
  return useMutation({
    mutationFn: (options: GoalsExportOptions) =>
      goalsApi.export(childId, options),
    onSuccess: (blob, options) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `goals-${childId}-${new Date().toISOString().split('T')[0]}.${options.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

// ============================================================================
// Combined Hook for Page State
// ============================================================================

interface UseGoalsPageOptions {
  childId: string;
  filters?: GoalFilters;
  sort?: GoalSortOptions;
}

export function useGoalsPage({ childId, filters, sort }: UseGoalsPageOptions) {
  const goalsQuery = useGoals(childId, filters, sort);
  const summaryQuery = useGoalsSummary(childId);
  
  const createMutation = useCreateGoal(childId);
  const updateMutation = useUpdateGoal(childId);
  const deleteMutation = useDeleteGoal(childId);
  const achieveMutation = useMarkGoalAchieved(childId);
  const discontinueMutation = useDiscontinueGoal(childId);
  const exportMutation = useExportGoals(childId);

  return {
    // Queries
    goals: goalsQuery.data?.items ?? [],
    total: goalsQuery.data?.total ?? 0,
    summary: summaryQuery.data,
    
    // Loading states
    isLoading: goalsQuery.isLoading,
    isSummaryLoading: summaryQuery.isLoading,
    
    // Error states
    error: goalsQuery.error,
    
    // Mutations
    createGoal: createMutation.mutateAsync,
    updateGoal: updateMutation.mutateAsync,
    deleteGoal: deleteMutation.mutateAsync,
    markAchieved: achieveMutation.mutateAsync,
    discontinue: discontinueMutation.mutateAsync,
    exportGoals: exportMutation.mutateAsync,
    
    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    
    // Refetch
    refetch: goalsQuery.refetch,
  };
}

/**
 * Combined hook for goal detail page
 */
interface UseGoalDetailOptions {
  goalId: string;
  childId: string;
}

export function useGoalDetail({ goalId, childId }: UseGoalDetailOptions) {
  const goalQuery = useGoal(goalId);
  const progressQuery = useGoalProgress(goalId);
  
  const updateMutation = useUpdateGoal(childId);
  const achieveMutation = useMarkGoalAchieved(childId);
  const discontinueMutation = useDiscontinueGoal(childId);
  const addProgressMutation = useAddProgressEntry(goalId, childId);
  const deleteProgressMutation = useDeleteProgressEntry(goalId, childId);

  return {
    // Queries
    goal: goalQuery.data,
    progress: progressQuery.data?.items ?? [],
    progressTotal: progressQuery.data?.total ?? 0,
    
    // Loading states
    isLoading: goalQuery.isLoading,
    isProgressLoading: progressQuery.isLoading,
    
    // Error states
    error: goalQuery.error,
    
    // Mutations
    updateGoal: (data: UpdateGoalRequest) => 
      updateMutation.mutateAsync({ goalId, data }),
    markAchieved: () => achieveMutation.mutateAsync(goalId),
    discontinue: (reason?: string) => 
      discontinueMutation.mutateAsync({ goalId, reason }),
    addProgress: addProgressMutation.mutateAsync,
    deleteProgress: deleteProgressMutation.mutateAsync,
    
    // Mutation states
    isUpdating: updateMutation.isPending,
    isAddingProgress: addProgressMutation.isPending,
    
    // Refetch
    refetchGoal: goalQuery.refetch,
    refetchProgress: progressQuery.refetch,
  };
}

// ============================================================================
// Simple Hooks (no childId required - for standalone use)
// ============================================================================

/**
 * Goal Detail Page Hook - standalone version
 */
export function useGoalDetailPage(goalId: string) {
  const goalQuery = useGoal(goalId);
  const progressQuery = useGoalProgress(goalId);
  
  return {
    goal: goalQuery.data ?? null,
    progress: progressQuery.data?.items ?? [],
    isLoading: goalQuery.isLoading || progressQuery.isLoading,
    error: goalQuery.error || progressQuery.error,
  };
}

/**
 * Achieve Goal Hook - standalone version
 */
export function useAchieveGoal() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (goalId: string) => goalsApi.markAchieved(goalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.all });
    },
  });
}

/**
 * Reopen Goal Hook
 */
export function useReopenGoal() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (goalId: string) => {
      // API call would go here
      return { id: goalId, status: 'active' };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.all });
    },
  });
}

/**
 * Create Progress Hook - standalone version
 */
export function useCreateProgress() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: { goalId: string; entry: CreateProgressEntryRequest }) => {
      return goalsApi.addProgress(data.goalId, data.entry);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.all });
    },
  });
}

/**
 * Add Goal Progress Hook - alias for useCreateProgress
 */
export function useAddGoalProgress() {
  return useCreateProgress();
}
/**
 * Delete Progress Hook - standalone version
 */
export function useDeleteProgress() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (entryId: string) => {
      return goalsApi.deleteProgress(entryId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.all });
    },
  });
}

